import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/OrderHistory.css";

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mars",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const MonthlyReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then(setAccessToken);
      if (user?.email) setUserEmail(user.email);
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const formatMonth = (dateStr) => {
    const d = new Date(dateStr);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  };

  const fetchReports = useCallback(async () => {
    if (!accessToken || !userEmail) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/reports`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setReports(data.reports || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError("Kunne ikke hente rapporter. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, accessToken, userEmail]);

  const downloadReport = async (reportId, month) => {
    try {
      const response = await fetch(`${API_URL}/reports/${reportId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `rapport_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading report:", err);
      alert("Kunne ikke laste ned rapport. Prøv igjen senere.");
    }
  };

  useEffect(() => {
    if (accessToken && userEmail) fetchReports();
  }, [accessToken, userEmail, fetchReports]);

  if (loading) {
    return (
      <div className="order-history">
        <h1>Månedlige rapporter</h1>
        <div className="loading">Laster inn rapporter...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-history">
        <h1>Månedlige rapporter</h1>
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchReports} className="retry-btn">
            Prøv på nytt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history">
      <h1>Månedlige rapporter</h1>
      {reports.length === 0 ? (
        <div className="no-orders">
          <p>Ingen rapporter tilgjengelig ennå.</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Periode</th>
                <th>Generert</th>
                <th>Handling</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{formatMonth(report.month)}</td>
                  <td>
                    {new Date(report.created_at).toLocaleDateString("nb-NO")}
                  </td>
                  <td className="order-action">
                    <button
                      className="download-btn"
                      onClick={() =>
                        downloadReport(report.id, report.month.slice(0, 7))
                      }
                    >
                      Last ned PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyReports;
